// ============================================
// Bygglov — Type Definitions
// ============================================

export type Tool = 'select' | 'wall' | 'door' | 'window' | 'erase';

export type SnapSize = 0.01 | 0.05 | 0.1 | 0.2 | 0.4 | 1.0;

export type WallType = 'exterior' | 'interior';

// Wall construction definitions
export interface WallConstruction {
  id: string;
  name: string;
  type: WallType;
  thickness: number; // mm
  description: string;
  layers: string[];
}

// A wall segment in the drawing
export interface Wall {
  id: string;
  type: 'wall';
  x1: number; // meters
  y1: number;
  x2: number;
  y2: number;
  construction: string; // WallConstruction.id
  layer: string;
  /**
   * Manual override for which side is "outside" on an exterior wall.
   * The default (false/undefined) outward normal is derived automatically
   * from the wall's own draw direction (x1,y1 -> x2,y2), which is already
   * self-consistent around a continuously-drawn perimeter — this flag only
   * exists for the cases where that default guess is wrong (disconnected
   * chains, manual edits, etc.) and needs a manual correction.
   */
  flipped?: boolean;
}

// Door placed on a wall
export interface Door {
  id: string;
  type: 'door';
  wallId: string;
  position: number; // 0-1 along wall length
  width: number; // meters (default 0.8)
  height: number; // meters (default 2.1)
  layer: string;
}

// Window placed on a wall
export interface Window {
  id: string;
  type: 'window';
  wallId: string;
  position: number; // 0-1 along wall length
  width: number; // meters (default 1.2)
  height: number; // meters (default 1.4)
  sillHeight: number; // meters from floor (default 0.9)
  layer: string;
}

export type DrawingObject = Wall | Door | Window;

// Layer definition
export interface Layer {
  id: string;
  name: string;
  visible: boolean;
  locked: boolean;
  group: 'construction' | 'content';
}

// Project data stored in Supabase
export interface ProjectData {
  objects: DrawingObject[];
  layers: Layer[];
  settings: ProjectSettings;
}

export interface ProjectSettings {
  gridSize: number; // meters
  snapSize: SnapSize;
  showGrid: boolean;
  showDimensions: boolean;
}

export interface Project {
  id: string;
  name: string;
  created_at: string;
  updated_at: string;
  data: ProjectData;
}

// Default values
export const DEFAULT_SNAP_SIZE: SnapSize = 0.1; // 10cm
export const DEFAULT_GRID_SIZE: number = 0.1; // 10cm grid

export const DEFAULT_OBJECTS: DrawingObject[] = [];
export const DEFAULT_LAYERS: Layer[] = [
  { id: 'construction', name: 'Konstruktion', visible: true, locked: false, group: 'construction' },
  { id: 'insulation', name: 'Isolering', visible: true, locked: false, group: 'construction' },
  { id: 'interior', name: 'Inredning', visible: true, locked: false, group: 'content' },
];
